import { AxiosInstance } from "axios";
import { NotificationPublisher } from "dots-ui/dotsNotifications";
import parse from "html-react-parser";
import i18next from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AvisConstants } from "../../../data/avisConstants";
import {
  PortalAuthenticationResult,
  PortalSettingsViewModel,
  RightsHolderInfoViewModel,
} from "../../../generated/data-contracts";
import { useSessionStorage } from "../../../hooks/useSessionStorage";
import {
  AvisUtils,
  IAxiosError,
  api,
  authApi,
  axiosApi,
  logoutApi,
  setupAuthErrorHandlerInterceptors,
  setupAxiosErrorHandlerInterceptors,
} from "../../../utils";
import { errorDataLoading } from "../appStates/appStates";
import { AvisContext } from "./AvisContext";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import _ from "lodash";

interface IAvisContextProviderProps {
  children?: React.ReactNode;
}

export function AvisContextProvider(
  props: Readonly<IAvisContextProviderProps>,
) {
  const { children } = props;

  /****************************** Boolean states. ******************************/

  const [shouldSelectRightHolder, setShouldSelectRightHolder] =
    useState<boolean>(false);

  const [isTextsLoading, setIsTextsLoading] = useState<boolean>(true);
  const [isAuthorsLoading, setIsAuthorsLoading] = useState<boolean>(false);
  const [isLogoutSubmitting, setIsLogoutSubmitting] = useState<boolean>(false);
  const [isResourcesLoading, setIsResourcesLoading] = useState<boolean>(true);
  const [isPortalSettingsLoading, setIsPortalSettingsLoading] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [error, setError] = useState<IAxiosError>();

  /****************************** Interceptors. ******************************/
  // response interceptors
  const [authApiInterceptorId, setAuthApiInterceptorId] = useState<number>();
  const [axiosApiInterceptorId, setAxiosApiInterceptorId] = useState<number>();
  const [apiInterceptorId, setApiInterceptorId] = useState<number>();

  // error interceptors
  const [apiErrorInterceptorId, setApiErrorInterceptorId] = useState<number>();
  const [authErrorInterceptorId, setAuthErrorInterceptorId] =
    useState<number>();
  const [axiosApiErrorInterceptorId, setAxiosApiErrorInterceptorId] =
    useState<number>();
  const [logoutApiErrorInterceptorId, setLogoutApiErrorInterceptorId] =
    useState<number>();

  /****************************** Session values. ******************************/

  const [sessionRightsHolder, setSessionRightsHolder] = useSessionStorage(
    AvisConstants.selected_rights_holder_storage_key,
  );
  const [sessionUser, setSessionUser] = useLocalStorage(
    AvisConstants.user_storage_key,
  );
  const [sessionRightsHolders, setSessionRightsHolders] = useSessionStorage(
    AvisConstants.rights_holders_storage_key,
  );
  const [sessionTexts, setSessionTexts] = useSessionStorage(
    AvisConstants.texts_storage_key,
  );
  const [sessionResources, setSessionResources] = useSessionStorage(
    AvisConstants.resources_storage_key,
  );
  const [ctxPortalSettings, setCtxPortalSettings] = useSessionStorage(
    AvisConstants.portal_settings_key,
  );

  /****************************** Context values. ******************************/
  const portalKind = AvisUtils.getPortalKind();

  const [ctxRightsHolder, setCtxRightsHolder] = useState<
    RightsHolderInfoViewModel | undefined
  >(sessionRightsHolder);

  const [ctxUser, setCtxUser] = useState<
    PortalAuthenticationResult | undefined
  >(sessionUser);

  const [ctxTextsToDisplay, setCtxTextsToDisplay] = useState<
    Record<string, string> | undefined
  >(sessionTexts);

  const [resourcesCollection, setResourcesCollection] = useState<
    Record<string, string> | undefined
  >(sessionResources);

  const [shouldSelectRightsHolder, setShouldSelectRightsHolder] =
    useState<boolean>(false);

  const [ctxRightsHolders, setCtxRightsHolders] = useState<
    RightsHolderInfoViewModel[] | undefined
  >(sessionRightsHolders);

  /****************************** Effects & Memos. ******************************/

  i18next.init({
    interpolation: {
      // React already does escaping
      escapeValue: true,
      prefix: "{",
      suffix: "}",
    },
    lng: "lv",
  });

  useEffect(() => {
    const handleInvalidToken = (e: any) => {
      if (
        e.key === AvisConstants.user_storage_key &&
        !!e.oldValue &&
        e.oldValue !== e.newValue
      ) {
        //If other tab is changing value to '' or to other user's info,
        //Remove ctx user in this instance to show login page
        //User is forced to autentificate again or refresh page
        setCtxUser(undefined);
      }
    };

    window.addEventListener("storage", handleInvalidToken);

    return function cleanup() {
      window.removeEventListener("storage", handleInvalidToken);
    };
  }, []);

  useEffect(() => {
    errorDataLoading.value = error;
  }, [error]);

  useEffect(() => {
    setIsLoading(
      isTextsLoading ||
        isResourcesLoading ||
        isAuthorsLoading ||
        isLogoutSubmitting ||
        isPortalSettingsLoading,
    );
  }, [
    isTextsLoading,
    isResourcesLoading,
    isAuthorsLoading,
    isLogoutSubmitting,
    isPortalSettingsLoading,
  ]);

  useEffect(() => {
    setIsTextsLoading(true);
    setIsResourcesLoading(true);

    if (error) {
      setIsTextsLoading(false);
      setIsResourcesLoading(false);

      return;
    }

    api
      .textsGetTextToDisplay({
        portalType: portalKind?.portalType ?? "unknown",
      })
      .then((res) => updateTextsToDisplay(res.data))
      .catch((err) => {
        setError(err);
        updateTextsToDisplay();
      })
      .finally(() => setIsTextsLoading(false));

    api
      .textsGetResources({ portalType: portalKind?.portalType ?? "unknown" })
      .then((res) => updateResources(res.data))
      .catch((err) => {
        setError(err);
        updateResources();
      })
      .finally(() => setIsResourcesLoading(false));

    if (portalKind && portalKind?.portalType !== "common") {
      setIsPortalSettingsLoading(true);
      api
        .securityPortalSettings()
        .then((res) => updateCtxPortalSettings(res.data))
        .catch((err) => {
          setError(err);
          updateCtxPortalSettings();
        })
        .finally(() => setIsPortalSettingsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!portalKind || (portalKind && portalKind.portalType != "authors"))
      return;

    setIsAuthorsLoading(true);

    if (error || !ctxUser || !ctxUser.isTermsAccepted || !ctxUser.isAuthor) {
      setIsAuthorsLoading(false);

      return;
    }

    // Getting rights holders
    if (sessionRightsHolders && typeof sessionRightsHolders === "object") {
      setIsAuthorsLoading(false);
    } else {
      api
        .rightsHoldersGetRightsHoldersData()
        .then((res) => {
          updateRightsHolders(res.data);

          // Forma autora izvēlei jārāda tikai tad, ja lietotājam ir saistoši vairāki tiesību īpašnieki (>1).
          if (res.data.length === 1) {
            updateRightsHolder(res.data[0]);
          }
        })
        .catch(setError)
        .finally(() => setIsAuthorsLoading(false));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxUser]);

  const isAuthorized = useMemo(() => {
    if (!ctxUser || typeof ctxUser !== "object") return false;

    return true;
  }, [ctxUser]);

  /****************************** Updaters. ******************************/

  const updateRightsHolder = useCallback(
    (value?: RightsHolderInfoViewModel) => {
      AvisUtils.sessionDataCleanup();
      setSessionRightsHolder(value);
      setCtxRightsHolder(value);
    },
    [setSessionRightsHolder],
  );

  const updateRightsHolders = useCallback(
    (value?: RightsHolderInfoViewModel[]) => {
      setSessionRightsHolders(value);
      setCtxRightsHolders(value);
    },
    [setSessionRightsHolders],
  );

  const updateUser = useCallback(
    (data?: PortalAuthenticationResult) => {
      if (_.isEqual(data, ctxUser)) return;
      setSessionUser(data);
      setCtxUser(data);
      updateRightsHolders();
    },
    [ctxUser, setSessionUser, updateRightsHolders],
  );

  const updateTextsToDisplay = (data?: Record<string, string>) => {
    setSessionTexts(data);
    setCtxTextsToDisplay(data);
  };

  const getResourceOptions = (options: string[]): { [key: string]: string } => {
    const indexedObject: { [key: string]: string } = {};

    options.forEach((option, index) => {
      indexedObject[index.toString()] = option;
    });

    return indexedObject;
  };

  const res = useCallback(
    (textKey: string, ...options: string[]): string => {
      if (!resourcesCollection) return "";

      if (typeof resourcesCollection !== "object") {
        // this case is handled in <AuthComponent>

        return "";
      }

      const resource = resourcesCollection[textKey];
      if (typeof resource === "undefined") {
        return `{${textKey}}`;
      }

      if (options && options.length !== 0) {
        return i18next.t(resource, getResourceOptions(options));
      }

      if (typeof options == "string") {
        return i18next.t(resource, {
          0: options,
        });
      }

      return resource;
    },
    [resourcesCollection],
  );

  const updateResources = (data?: Record<string, string>) => {
    setSessionResources(data);
    setResourcesCollection(data);

    if (data && authErrorInterceptorId === undefined)
      setAuthErrorInterceptorId(setupAuthErrorHandlerInterceptors(res));
  };
  const updateCtxPortalSettings = (data?: PortalSettingsViewModel) => {
    setCtxPortalSettings(data);
  };

  /****************************** Handlers. ******************************/

  const resetRightsHolder = useCallback(() => {
    if (ctxRightsHolders?.length === 1) return;
    updateRightsHolder();
    setShouldSelectRightsHolder(true);
  }, [ctxRightsHolders?.length, updateRightsHolder]);

  const removeAxiosInterceptors = useCallback(() => {
    if (authApiInterceptorId !== undefined) {
      authApi.interceptors.request.eject(authApiInterceptorId);
      setAuthApiInterceptorId(undefined);
    }

    if (axiosApiInterceptorId !== undefined) {
      axiosApi.interceptors.request.eject(axiosApiInterceptorId);
      setAxiosApiInterceptorId(undefined);
    }

    if (apiInterceptorId !== undefined) {
      api.instance.interceptors.request.eject(apiInterceptorId);
      setApiInterceptorId(undefined);
    }
  }, [apiInterceptorId, authApiInterceptorId, axiosApiInterceptorId]);

  const clearSesionAndContext = useCallback(() => {
    removeAxiosInterceptors();
    updateUser();
    updateRightsHolder();
    updateRightsHolders();
    setError(undefined);
    AvisUtils.sessionDataCleanup();
    localStorage.removeItem(AvisConstants.user_last_activity_time_key);
  }, [
    removeAxiosInterceptors,
    updateRightsHolder,
    updateRightsHolders,
    updateUser,
  ]);

  const logout = useCallback(() => {
    if (error) return;

    setIsLogoutSubmitting(true);
    clearSesionAndContext();

    logoutApi
      .post(`/Auth/Logout`)
      .then(() => NotificationPublisher.closeAllToasts())
      .finally(() => {
        clearSesionAndContext();
        setIsLogoutSubmitting(false);
      });
  }, [clearSesionAndContext, error]);

  const login = useCallback(
    (data?: PortalAuthenticationResult) => {
      if (!data) {
        logout();

        return;
      }

      updateUser(data);
    },
    [logout, updateUser],
  );

  const getText = useCallback(
    (textKey: string): string | JSX.Element[] | JSX.Element | null => {
      if (!ctxTextsToDisplay) return null;

      if (typeof ctxTextsToDisplay !== "object") {
        // this case is handled in <AuthComponent>

        return null;
      }

      if (typeof ctxTextsToDisplay[textKey] === "undefined") {
        return `Nedefinēts paskaidrojošais teksts '${textKey}'!`;
      }
      const textValue = ctxTextsToDisplay[textKey];

      if (!textValue) return null;

      return parse(textValue);
    },
    [ctxTextsToDisplay],
  );

  const setupInterceptor = (axiosInstance: AxiosInstance): number => {
    const test = axiosInstance.interceptors.request.use(
      (config) => {
        AvisUtils.updateLastActivityTime();

        return config;
      },
      (error) => Promise.reject(error),
    );

    return test;
  };

  const setupAxiosInterceptors = useCallback(() => {
    removeAxiosInterceptors();
    setApiInterceptorId(setupInterceptor(api.instance));
    setAxiosApiInterceptorId(setupInterceptor(axiosApi));
    setAuthApiInterceptorId(setupInterceptor(authApi));
  }, [removeAxiosInterceptors]);

  const setupErrorInterceptors = useCallback(() => {
    if (apiErrorInterceptorId === undefined) {
      setApiErrorInterceptorId(
        setupAxiosErrorHandlerInterceptors(api.instance, res, logout),
      );
    }

    if (axiosApiErrorInterceptorId === undefined) {
      setAxiosApiErrorInterceptorId(
        setupAxiosErrorHandlerInterceptors(axiosApi, res, logout),
      );
    }

    if (logoutApiErrorInterceptorId === undefined) {
      setLogoutApiErrorInterceptorId(
        setupAxiosErrorHandlerInterceptors(logoutApi, res),
      );
    }
  }, [
    apiErrorInterceptorId,
    axiosApiErrorInterceptorId,
    logout,
    logoutApiErrorInterceptorId,
    res,
  ]);
  const contextValues = useMemo(() => {
    return {
      portalKind: portalKind,
      isAuthorized,
      ctxUser,
      ctxRightsHolder,
      ctxTextsToDisplay,
      isLoading,
      ctxRightsHolders,
      shouldSelectRightsHolder,
      shouldSelectRightHolder,
      ctxPortalSettings,
      setupAxiosInterceptors,
      setShouldSelectRightsHolder,
      setShouldSelectRightHolder,
      resetRightsHolder,
      updateRightsHolder,
      updateUser,
      logout,
      login,
      getText,
      res,
      setupErrorInterceptors,
    };
  }, [
    ctxPortalSettings,
    ctxRightsHolder,
    ctxRightsHolders,
    ctxTextsToDisplay,
    ctxUser,
    getText,
    isAuthorized,
    isLoading,
    login,
    logout,
    portalKind,
    res,
    setupAxiosInterceptors,
    setupErrorInterceptors,
    shouldSelectRightHolder,
    shouldSelectRightsHolder,
    resetRightsHolder,
    updateRightsHolder,
    updateUser,
  ]);

  return (
    <AvisContext.Provider value={contextValues}>
      {children}
    </AvisContext.Provider>
  );
}
